import React, { ChangeEvent, useEffect, useState } from 'react'
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { contactUsValidateEmailAddress, contactUsValidateFirstName, contactUsValidateLastName, contactUsValidateMessage } from '../validations/contact/contact';
import { Button, FormField, Loader } from '../';
import { actionCreateContactUsMessage } from '../../actions/contactUs';
import { AppDispatch } from '../../reducers/store';

import './contactForm.css'

interface PostDataParams {
  firstName: string;
  lastName: string
  emailAddress: string
  message: string
}

const ContactForm = () => {
  const dispatch                                    = useDispatch<AppDispatch>();
  const [savingInfo, setSavingInfo]                 = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled]     = useState<boolean>(true);
  const [errorMessage, setErrorMessage]             = useState(null);
  const [firstNameErrors, setFirstNameErrors]       = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors]         = useState<string[]>([]);
  const [emailAddressErrors, setEmailAddressErrors] = useState<string[]>([]);
  const [messageErrors, setMessageErrors]           = useState<string[]>([]);
  const [captchaValue, setCaptchaValue]             = useState(null);

  const [postData, setPostData]         = useState<PostDataParams>({firstName: '', lastName: '', emailAddress: '', message: ''})
  const [isLoadingBtn, setIsLoadingBtn] = useState({firstName: false, lastName: false, emailAddress: false, message: false })
  const [isValid, setIsValid]           = useState({firstName: false, lastName: false, emailAddress: false, message: false })

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500)

  const handleChangeCaptcha = (value: any) => {
    setCaptchaValue(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  useEffect(() => {
    const validateFirstName = () => {
      const { isValid, errors } = contactUsValidateFirstName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        firstName: isValid,
      }));
      setFirstNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        firstName: false,
      }));
      return errors;
    };

    const validateLastName = () => {
      const { isValid, errors } = contactUsValidateLastName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        lastName: isValid,
      }));
      setLastNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        lastName: false,
      }));
      return errors;
    };

    const validateEmailAddress = () => {
      const { isValid, errors } = contactUsValidateEmailAddress({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        emailAddress: isValid,
      }));
      setEmailAddressErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        emailAddress: false,
      }));
      return errors;
    };

    const validateMessage = () => {
      const { isValid, errors } = contactUsValidateMessage({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        message: isValid,
      }));
      setMessageErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        message: false,
      }));
      return errors;
    };
    
    const firstNameErrors      = validateFirstName();
    const lastNameErrors       = validateLastName();
    const emailAddressErrors   = validateEmailAddress();
    const messageErrors        = validateMessage()
    
    setFirstNameErrors(firstNameErrors);
    setLastNameErrors(lastNameErrors);
    setEmailAddressErrors(emailAddressErrors);
    setMessageErrors(messageErrors);

      const hasFormErrors = firstNameErrors.length > 0 || lastNameErrors.length > 0 || emailAddressErrors.length > 0 || messageErrors.length > 0
    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData, captchaValue]);

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    setIsButtonDisabled(true)
    setSavingInfo(true);
    const res = await dispatch(actionCreateContactUsMessage({postData}));
    console.log(res)
    try{
      if (res.success === true) {
        toast.success("message sent successfully");
        setErrorMessage(null);
        setPostData({firstName: '', lastName: '', emailAddress: '', message: ''})
        setSavingInfo(false)
      } else if (res?.status === 400) {
        setErrorMessage(res?.message);
        setSavingInfo(false)
      }
    }catch(error: any){
      setErrorMessage(res?.status)
      setSavingInfo(false)
    }
    
  }

  return (
    <>
      <form className='contact-reg-container' onSubmit={handleSubmit} autoComplete="off">
        <div className='contact-login-container-title'>
            <h3>Send a message</h3>
            <small>We'd love help</small>
        </div>

        <div className=''>
        <FormField inputType type="text" labelName="First Name" name="firstName" value={postData.firstName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.firstName} isValid={isValid.firstName} errors={firstNameErrors || []} />

        <FormField inputType type="text" labelName="Last Name" name="lastName" value={postData.lastName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.lastName} isValid={isValid.lastName} errors={lastNameErrors || []} />

        <FormField inputType type="text" labelName="Email Address" name="emailAddress" value={postData.emailAddress} handleChange={handleChange} isLoadingBtn={isLoadingBtn.emailAddress} isValid={isValid.emailAddress} errors={emailAddressErrors || []} />

        <FormField textareaType maxLength={1000} labelName="Message" name="message" value={postData.message} handleChange={handleChange} isLoadingBtn={isLoadingBtn.message} isValid={isValid.message} errors={messageErrors || []} />

        {/* <div className='contact-captcha'><ReCAPTCHA sitekey="6LdOKzwnAAAAAFbP4KMhcR-XyF8IFEKYkJ6g-IM-" onChange={handleChangeCaptcha}/></div> */}

        <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Sending...</> : "Send"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />

        </div>
        {errorMessage && <p className='error-msg'>{errorMessage}</p>}
      </form>
    </>
  )
}

export default ContactForm